import { intervalsOverlap } from "../../../lib/intervals";

/**
 * Session-vs-session double-booking detection for the dashboard. Two sessions
 * conflict when their winning-slot time ranges OVERLAP IN ABSOLUTE TIME (the
 * canonical half-open interval test). This is deliberately a raw-instant overlap,
 * NOT local-day bucketing: a real double-book is the same for every viewer in
 * every zone, and two non-overlapping sessions on the same calendar day are not
 * a conflict. (Local-day bucketing is for the availability *day pip*, a different
 * question.)
 *
 * Returns a `Map<sessionId, string[]>` of the titles each session conflicts with.
 * This is the SINGLE producer behind both the dashboard calendar grid
 * (DashboardCalendar) and the mobile agenda (MobileAgendaView) — feeding both
 * from one map is what stops the surfaces from disagreeing (previously the grid
 * also paired past sessions and used display-shifted dates, so the same pair
 * could badge on the phone but not the desktop). Callers scope the input set
 * (e.g. upcoming-finalized only) — this helper does not filter by status/time.
 *
 * @param {Array<{id: string, title?: string, winningSlot?: {start: any, end: any}}>} sessions
 * @returns {Map<string, string[]>}
 */
export function detectSessionConflicts(sessions = []) {
  const conflicts = new Map();
  const withSlots = (sessions || []).filter(
    (s) => s?.id && s.winningSlot?.start && s.winningSlot?.end
  );

  for (let i = 0; i < withSlots.length; i++) {
    for (let j = i + 1; j < withSlots.length; j++) {
      const a = withSlots[i];
      const b = withSlots[j];
      if (
        intervalsOverlap(
          a.winningSlot.start,
          a.winningSlot.end,
          b.winningSlot.start,
          b.winningSlot.end
        )
      ) {
        if (!conflicts.has(a.id)) conflicts.set(a.id, []);
        if (!conflicts.has(b.id)) conflicts.set(b.id, []);
        conflicts.get(a.id).push(b.title || "Untitled");
        conflicts.get(b.id).push(a.title || "Untitled");
      }
    }
  }

  return conflicts;
}
